import type { AlertColor } from '@mui/material';
import { z } from 'zod';

export const toastSchema = z.object({
  id: z.string(),
  message: z.string(),
  severity: z.enum(['error', 'warning', 'info', 'success'] as [
    AlertColor,
    ...AlertColor[],
  ]),
  title: z.string(),
});
export type Toast = z.infer<typeof toastSchema>;

export const TOAST_DURATION: Record<Toast['severity'], number> = {
  error: 6000,
  info: 2000,
  success: 2000,
  warning: 4000,
};

/** Converts an unknown thrown value to a validated Toast[].
 *  Each candidate is parsed with toastSchema.safeParse. Items that fail
 *  (e.g. plain Error throws from transformers) get a safe fallback toast
 *  using the error message when available, so the UI never renders
 *  undefined fields. */
export function toSafeToasts(error: unknown): Toast[] {
  const candidates = Array.isArray(error) ? error : [error];
  return candidates.map((candidate) => {
    const result = toastSchema.safeParse(candidate);
    if (result.success) return result.data;
    return {
      id: '',
      message:
        candidate instanceof Error
          ? candidate.message
          : 'An unexpected error occurred.',
      severity: 'error' as const,
      title: 'Error',
    };
  });
}
